import { Form, Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { Box, Button, Grid, Menu, Typography } from '@mui/material';
import { SERVICES } from '@shared/constants/apiConfig';
import { Modal } from '@shared/components/modals/Modal';
import {
  IDeal,
  IDocumentsDmvPackate,
  ITitleDocuments,
} from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';
import { requestSaveTitleDocGen } from '@modules/ContactManager/components/Tabs/components/Deals/queries';
import { refreshWorkflow, setLastCommentsRefresh, setRefreshWorkflow } from '@store/reducers/contactManagerSlice';
import { SectionContainer, WithBG } from '@styles/StyledDiv';
import { TextWrapper } from '@styles/CommonStyles';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { requestPayoffStatus } from '../queries';
import { DocsTitleMenu } from './DocsTitleMenu';
import { TitleDocumentsForm } from './TitleDocumentsForm';

interface ITitlesModalProps {
  onClose: () => void;
  dealId: string;
  assetId: string;
  titleModalPosition: number;
}

export const TitlesModal = ({ onClose, dealId, assetId, titleModalPosition }: ITitlesModalProps) => {
  const { isAdmin, userHasRoles } = useUserAccount();
  const refreshWorkflowSection = useSelector(refreshWorkflow);
  const dispatch = useDispatch();
  const { data: leftPart, mutate } = useSWR<ITitleDocuments>(
    `${SERVICES.DOCUMENT}/documents/${dealId}/titledocgenindex`,
  );
  const { data: rightPart } = useSWR<IDocumentsDmvPackate>(
    `${SERVICES.DOCUMENT}/state-title-document/${dealId}/dmv-packet`,
  );

  const { data: deal } = useSWR<IDeal>(dealId && `/${SERVICES.DEAL}/deals/${dealId}`);
  const hasAcumaticaATSubmitRole = isAdmin || userHasRoles([Roles.ACUMATICA_AT_SUBMIT]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [payoffStatus, setPayoffStatus] = useState<string>();
  const [titleStatus, setTitleStatus] = useState<string>();
  const [trackNumber, setTrackNumber] = useState<string>();
  const open = Boolean(anchorEl);

  const selectedItems = useMemo(
    () => (leftPart?.items ?? []).filter((item) => item.isReceived).map((item) => item.documentName),
    [leftPart?.items],
  );

  const handleDocsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    dispatch(setLastCommentsRefresh(new Date().toISOString()));
  };

  const handleSubmit = useCallback(async(data: ITitleDocuments) => {
    const selected = data.items.map((document) => ({
      ...document,
      isReceived: data.selectedItems.includes(document.documentName),
    }));

    setLoading(true);

    await requestSaveTitleDocGen({
      ...data,
      items: selected,
      comment: {
        ...data.comment,
        createdDate: new Date().toISOString(),
        comment: data.comment?.comment || '',
      },
    });

    mutate();
    setLoading(false);
  }, []);

  useEffect(() => {
    setPayoffStatus(deal?.payoffInformation?.payoffStatus);
    setTrackNumber(deal?.payoffInformation?.trackingNumber);
    setTitleStatus(deal?.payoffInformation?.titleType);
  },[deal]); 

  const handlePayoffStatus = useCallback(async() => {
    setLoading(true);

    const respsone = await requestPayoffStatus(dealId);
    if (respsone) {
      setPayoffStatus(respsone?.status);
      setTitleStatus(respsone?.titleType);
      if (respsone?.titleType === 'PAPER')
        setTrackNumber(respsone?.trackingNo);
      else
        setTrackNumber('');

      dispatch(setRefreshWorkflow(refreshWorkflowSection));
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
    }
    setLoading(false);
  }, [refreshWorkflowSection]);

  return (
    <Modal 
      open
      hideBackdrop
      isLoading={Boolean(loading || !leftPart)}
      resetPosition={titleModalPosition}
      title="Titles"
      dialogWidth="1350px" 
      onClose={onClose}
    >
      <>
        <Grid container spacing={4} >
          <Grid item xs={6} className="bordered">
            <Formik
              enableReinitialize
              initialValues={{ ...leftPart, selectedItems } as ITitleDocuments}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <Form onSubmit={props.handleSubmit}>
                  <TitleDocumentsForm values={props.values} />
                </Form>
              )}
            </Formik>
          
          </Grid>
          <Grid item xs={6}>
            <SectionContainer>
              DMV Packet
              <Button
                variant="contained"
                endIcon={<ChevronDownIcon width={16} color="#fff" />}
                onClick={handleDocsClick}
              >
                Docs
              </Button>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                onClose={handleClose}
              >
                <DocsTitleMenu
                  handleClose={handleClose}
                  assetId={assetId}
                  setLoading={setLoading}
                />
              </Menu>
            </SectionContainer>

            <WithBG>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  State:
                </Grid>

                <Grid item xs={9}>
                  {rightPart?.assetState}
                </Grid>

                <Grid item xs={3}>
                  Deal Type:
                </Grid>

                <Grid item xs={9}>
                  {rightPart?.dealBorrowerType}
                </Grid>
              </Grid>
            </WithBG>
            {rightPart?.documentNames.map((names, index) => {
              return (
                <Typography key={index} sx={{ wordBreak: 'break-word' }}>
                  {names}
                </Typography>
              );
            })}
          </Grid>
          <Grid item xs={6} className="bordered">
          <SectionContainer>
            <Typography variant='h4'>
              DT Payoff Status: 
              <Typography variant='body1'>{payoffStatus}</Typography>
            </Typography>
            
            <Button
                variant="contained"
                disabled={!hasAcumaticaATSubmitRole}
                onClick={() => handlePayoffStatus()}>
                Get Status
            </Button>
          </SectionContainer>
          { titleStatus === 'PAPER' && 
            <TextWrapper flexDirection='row' marginBottom='10px'> 
             <Typography variant='h4'>FedEx # :</Typography> 
              <Box sx={{marginLeft: '20px'}} >{trackNumber}</Box>
            </TextWrapper>
          }
            <TextWrapper flexDirection='row'> 
              <Typography variant='h4'>Title Status :</Typography> 
              <Box sx={{marginLeft: '20px'}} >{titleStatus}</Box>
            </TextWrapper>
          </Grid>
        </Grid>
      </>
    </Modal>
  );
};
